// FEEDBACK_WIDGET — delete this file when done testing
import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';

const API = (path, opts = {}) =>
  fetch(path, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }, ...opts });

const TYPE_COLOURS  = { bug: ['#fee2e2','#991b1b'], feature: ['#ede9fe','#5b21b6'] };
const STATUS_COLOURS = { new: ['#fef3c7','#92400e'], reviewed: ['#dbeafe','#1e40af'], done: ['#d1fae5','#065f46'] };
const TYPE_ICONS    = { bug: '🐛', feature: '✨' };

export default function OwnerFeedback() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [typeFilter, setType]   = useState('');
  const [statusFilter, setStatus] = useState('new');
  const [updating, setUpdating] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (typeFilter)   params.set('type',   typeFilter);
    if (statusFilter) params.set('status', statusFilter);
    const r = await API(`/api/feedback?${params}`);
    const d = await r.json();
    if (!r.ok) { setError(d.error || 'Failed'); setLoading(false); return; }
    setItems(Array.isArray(d) ? d : []);
    setLoading(false);
  }, [typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setItemStatus = async (item, newStatus) => {
    setUpdating(u => ({...u, [item.id]: true}));
    const r = await API(`/api/feedback/${item.id}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) });
    if (r.ok) setItems(prev => prev.map(i => i.id === item.id ? {...i, status: newStatus} : i));
    setUpdating(u => ({...u, [item.id]: false}));
  };

  const counts = items.reduce((acc, i) => { acc[i.status] = (acc[i.status]||0)+1; return acc; }, {});
  const bugCount     = items.filter(i => i.type === 'bug').length;
  const featureCount = items.filter(i => i.type === 'feature').length;

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        {/* Testing-phase banner */}
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', padding: '10px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#92400e' }}>
          <span style={{ fontSize: '16px' }}>🧪</span>
          <span><strong>Testing phase widget</strong> — this page and the feedback button will be removed before public launch. Search <code style={{ background: '#fde68a', padding: '1px 5px', borderRadius: '3px' }}>FEEDBACK_WIDGET</code> in the codebase to find all removal points.</span>
        </div>

        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Feedback Inbox</h1>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>Bug reports and feature requests from testers</p>

        {error && <div style={{ background:'#fee2e2', color:'#991b1b', padding:'10px 14px', borderRadius:'8px', fontSize:'13px', marginBottom:'16px' }}>{error}</div>}

        {/* Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '10px', marginBottom: '20px' }}>
          {[
            ['Total', items.length, '#e5e7eb', '#374151'],
            ['🐛 Bugs', bugCount, '#fee2e2', '#991b1b'],
            ['✨ Features', featureCount, '#ede9fe', '#5b21b6'],
            ['New', counts.new||0, '#fef3c7', '#92400e'],
            ['Done', counts.done||0, '#d1fae5', '#065f46'],
          ].map(([label,val,bg,col]) => (
            <div key={label} style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:'10px', padding:'14px', textAlign:'center' }}>
              <div style={{ fontSize:'22px', fontWeight:800, color:col, fontFamily:'Barlow Condensed,sans-serif' }}>{val}</div>
              <div style={{ fontSize:'11px', color:'#6b7280', marginTop:'3px' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {[['','All types'],['bug','🐛 Bugs'],['feature','✨ Features']].map(([val,label]) => (
            <button key={val} onClick={() => setType(val)} style={{
              border:'none', borderRadius:'999px', padding:'6px 14px', cursor:'pointer', fontSize:'12px',
              fontWeight: typeFilter===val ? 700 : 500,
              background: typeFilter===val ? '#0e1b2c' : '#e5e7eb',
              color: typeFilter===val ? '#fff' : '#374151',
            }}>{label}</button>
          ))}
          <div style={{ width: '1px', background: '#e5e7eb', margin: '0 4px' }} />
          {[['','All statuses'],['new','New'],['reviewed','Reviewed'],['done','Done']].map(([val,label]) => (
            <button key={val} onClick={() => setStatus(val)} style={{
              border:'none', borderRadius:'999px', padding:'6px 14px', cursor:'pointer', fontSize:'12px',
              fontWeight: statusFilter===val ? 700 : 500,
              background: statusFilter===val ? '#1d4ed8' : '#dbeafe',
              color: statusFilter===val ? '#fff' : '#1e40af',
            }}>{label}</button>
          ))}
        </div>

        {/* List */}
        <div style={{ display: 'grid', gap: '10px' }}>
          {loading ? (
            <div style={{ padding:'40px', textAlign:'center', color:'#6b7280' }}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{ padding:'40px', textAlign:'center', color:'#9ca3af', background:'#fff', borderRadius:'12px', border:'1px solid #e5e7eb' }}>No feedback found</div>
          ) : items.map(item => {
            const [typeBg, typeCol] = TYPE_COLOURS[item.type]  || ['#f3f4f6','#6b7280'];
            const [stBg,   stCol]  = STATUS_COLOURS[item.status] || ['#f3f4f6','#6b7280'];
            return (
              <div key={item.id} style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', padding:'16px 18px' }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:'12px' }}>
                  <span style={{ fontSize:'22px', flexShrink:0, marginTop:'1px' }}>{TYPE_ICONS[item.type]}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'6px' }}>
                      <span style={{ background:typeBg, color:typeCol, fontSize:'11px', fontWeight:700, padding:'2px 8px', borderRadius:'999px', textTransform:'capitalize' }}>
                        {item.type === 'bug' ? 'Bug Report' : 'Feature Request'}
                      </span>
                      <span style={{ background:stBg, color:stCol, fontSize:'11px', fontWeight:700, padding:'2px 8px', borderRadius:'999px', textTransform:'capitalize' }}>
                        {item.status}
                      </span>
                      <span style={{ fontSize:'11px', color:'#9ca3af', fontFamily:'monospace' }}>{item.page_url || '—'}</span>
                      <span style={{ fontSize:'11px', color:'#9ca3af', marginLeft:'auto' }}>
                        {new Date(item.created_at).toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
                      </span>
                    </div>
                    <div style={{ fontSize:'14px', color:'#111827', lineHeight:'1.5', marginBottom:'8px' }}>{item.description}</div>
                    <div style={{ fontSize:'12px', color:'#9ca3af' }}>
                      {item.user_name || 'Unknown user'} · {item.user_role || '—'}
                    </div>
                  </div>
                  {/* Status actions */}
                  <div style={{ display:'flex', flexDirection:'column', gap:'6px', flexShrink:0 }}>
                    {item.status === 'new' && (
                      <button disabled={updating[item.id]} onClick={() => setItemStatus(item,'reviewed')}
                        style={{ background:'#dbeafe', color:'#1e40af', border:'none', borderRadius:'6px', padding:'5px 12px', fontSize:'11px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                        Mark Reviewed
                      </button>
                    )}
                    {item.status !== 'done' && (
                      <button disabled={updating[item.id]} onClick={() => setItemStatus(item,'done')}
                        style={{ background:'#d1fae5', color:'#065f46', border:'none', borderRadius:'6px', padding:'5px 12px', fontSize:'11px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                        Mark Done
                      </button>
                    )}
                    {item.status === 'done' && (
                      <button disabled={updating[item.id]} onClick={() => setItemStatus(item,'new')}
                        style={{ background:'#f3f4f6', color:'#6b7280', border:'none', borderRadius:'6px', padding:'5px 12px', fontSize:'11px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                        Reopen
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
